import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Dashboard.css';

const StatCard = ({ icon, label, value, subvalue, onClick }) => (
  <div className={`stat-card ${onClick ? 'clickable' : ''}`} onClick={onClick}>
    <div className="stat-header">
      <div className="stat-icon-wrapper">
        {icon}
      </div>
    </div>
    <div className="stat-label">{label}</div>
    <div className="stat-value">
      {value} {subvalue && <span className="stat-subvalue">/ {subvalue}</span>}
    </div>
  </div>
);

const Dashboard = ({ onPageChange }) => {
  const [stats, setStats] = useState({
    roundUpAccumulation: 0,
    loansRepaid: 0,
    subscribedUsers: 0,
    totalUsers: 0,
    totalRevenue: 0,
    loanPaymentHistory: []
  });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin-management/dashboard-stats');
        const resData = response.data?.data || response.data;
        setStats({
          roundUpAccumulation: resData?.totalRoundups ?? resData?.roundUpAccumulation ?? 0,
          loansRepaid: resData?.totalLoansRepaid ?? resData?.loansRepaid ?? 0,
          subscribedUsers: resData?.subscribedUsers ?? 0,
          totalUsers: resData?.totalUsers ?? 0,
          totalRevenue: resData?.totalRevenue ?? 0,
          loanPaymentHistory: resData?.loanPaymentHistory ?? []
        });
      } catch (error) {
        if (error.response && error.response.status === 403) {
          localStorage.clear();
          window.location.href = '/login';
          return;
        }
        console.error('Failed to fetch dashboard stats:', error);
      }
    };

    const fetchPayments = async () => {
      try {
        const response = await api.get('/admin-management/payments');
        setPayments(response.data?.payments || []);
      } catch (error) {
        console.error('Failed to fetch recent payments:', error);
      }
    };

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchPayments()]);
      setLoading(false);
    };

    loadData();
  }, []);

  const formatValue = (val, isCurrency = true) => {
    if (loading) return "...";
    const num = Number(val) || 0;
    
    if (isCurrency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: num % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(num);
    }
    return num.toLocaleString();
  };

  const getWeeklyStats = () => {
    const history = stats.loanPaymentHistory || [];
    const weeks = [0, 0, 0, 0];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    history.forEach(item => {
      const date = new Date(item.paymentDate || item.createdAt);
      if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
        const day = date.getDate();
        if (day <= 7) weeks[0] += Number(item.amount);
        else if (day <= 14) weeks[1] += Number(item.amount);
        else if (day <= 21) weeks[2] += Number(item.amount);
        else weeks[3] += Number(item.amount);
      }
    });

    const maxAmount = Math.max(...weeks, 10);
    const data = weeks.map((amount, index) => ({
      week: `Wk ${index + 1}`,
      amount: amount,
      height: amount > 0 ? `${Math.max((amount / maxAmount) * 100, 5)}%` : '0%',
      formattedAmount: amount >= 1000 ? `${(amount / 1000).toFixed(1)}k` : amount.toString()
    }));

    return { data, maxAmount };
  };

  const { data: weeklyData, maxAmount } = getWeeklyStats();

  const getYAxisLabels = () => {
    const labels = [];
    for (let i = 0; i <= 5; i++) {
      const val = (maxAmount * i) / 5;
      labels.push(val >= 1000 ? `${(val / 1000).toFixed(1)}k` : Math.round(val).toString());
    }
    return labels;
  };

  const yAxisLabels = getYAxisLabels();

  return (
    <div className="dashboard-view-content">
      <div className="overview-section">
        <div className="overview-header">
          <h2 className="section-title">Financial Overview</h2>
        </div>

        <div className="stats-grid">
          <StatCard 
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
            label="Round-Up Accumulation"
            value={formatValue(stats.roundUpAccumulation)}
            onClick={() => onPageChange('ledger')}
          />
          <StatCard 
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>}
            label="Loans Repaid"
            value={formatValue(stats.loansRepaid)}
            onClick={() => onPageChange('loans')}
          />
          <StatCard 
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
            label="Subscribed Users"
            value={formatValue(stats.subscribedUsers, false)}
            subvalue={formatValue(stats.totalUsers, false)}
            onClick={() => onPageChange('users')}
          />
          <StatCard 
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
            label="Total Revenue"
            value={formatValue(stats.totalRevenue)}
            onClick={() => onPageChange('subscription')}
          />
        </div>
      </div>

      <div className="content-grid">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Loan Payments</h3>
            {/* <div className="card-filters">
              <select className="filter-select"><option>All Users</option></select>
              <select className="filter-select"><option>Weekly</option></select>
            </div> */}
          </div>
          <div className="table-responsive">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Paid Date</th>
                  <th>Amount</th>
                  <th>Account Number</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" style={{ textAlign: 'center', padding: '24px' }}>Loading payments...</td></tr>
                ) : payments.length === 0 ? (
                  <tr><td colSpan="4" style={{ textAlign: 'center', padding: '24px' }}>No recent payments found.</td></tr>
                ) : payments.map((payment) => {
                  const user = payment.loanAccount?.loanAccountUser;
                  const loan = payment.loanAccount;
                  return (
                    <tr key={payment.id}>
                      <td>{user?.firstName} {user?.lastName}</td>
                      <td>{new Date(payment.paymentDate || payment.createdAt).toLocaleDateString('en-US')}</td>
                      <td>{formatValue(payment.amount)}</td>
                      <td>{loan?.accountNumber || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Loans Payment Overview – Current Month</h3>
            {/* <select className="filter-select"><option>This Month</option></select> */}
          </div>
          <div className="chart-container">
            <div className="chart-y-axis">
              {yAxisLabels.map((label, i) => <span key={i}>{label}</span>)}
            </div>
            {weeklyData.map((data, idx) => (
              <div key={idx} className="bar-wrapper">
                <div className="bar" style={{ height: '100%', background: '#F4F4F4' }}>
                  <div className="bar-fill" style={{ 
                    height: data.height, 
                    background: 'var(--primary-color)',
                    opacity: 1 - (idx * 0.15), // Subtle gradient across weeks
                    borderRadius: '8px'
                  }}></div>
                </div>
              </div>
            ))}
          </div>
          <div className="chart-labels">
            {weeklyData.map((data, idx) => (
              <div key={idx} className="label-item">
                <span className="label-percent">{data.formattedAmount}</span>
                <span className="label-text">{data.week}</span>
              </div>
            ))}
         </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
